'use client';

import { Button, FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { AddressLookupResult } from '../../Forms/Registration/types';
import { AddressLookup } from '../AddressLookup/components/AddressLookup';
import { AddressLookupWithConfig } from '../AddressLookup/components/AddressLookupWithConfig';
import { FieldInfo } from '../FieldInfo/FieldInfo';
import { Select } from '../Select/Select';
import { TextInput } from '../TextInput/TextInput';
import type { AddressFormFieldName, AddressFormValues } from './types';

type RegistrationAddressFormFieldsProps = {
  formIdPrefix: string;
  showManualEntry: boolean;
  onLookupOpenChange: (open: boolean) => void;
  onAddressSelect: (address: AddressLookupResult) => void;
  onToggleEntryMode: () => void;
  enterManuallyLabel: string;
  switchToSearchLabel: string;
  lookupVariant: 'registration' | 'profile';
  submitAttempted?: boolean;
  onLookupFieldChange?: (value: string) => void;
  onDropdownHeightChange?: (height: number) => void;
  enterManuallyTrackingName?: string;
};

function RegistrationAddressFormFields({
  formIdPrefix,
  showManualEntry,
  onLookupOpenChange,
  onAddressSelect,
  onToggleEntryMode,
  enterManuallyLabel,
  switchToSearchLabel,
  lookupVariant,
  submitAttempted = false,
  onLookupFieldChange,
  onDropdownHeightChange,
  enterManuallyTrackingName = 'Address Manually',
}: RegistrationAddressFormFieldsProps) {
  const t = useTranslations('form');
  const form = useFormContext<AddressFormValues>();
  const {
    formState: { errors: formErrors },
  } = form;
  const market = getSiteConfig().REGISTRATION;
  const collectsAddressLine2 = market.collectsAddressLine2;
  const showsStateSelect = market.showsStateSelect;
  const usesStackedCityPostcodeLayout = market.states.length > 0;

  const sortedStates = useMemo(
    () => [...market.states].sort((stateA, stateB) => stateA.value.localeCompare(stateB.value)).map(state => ({ value: state.value, text: state.value })),
    [market.states]
  );

  const getAddressFieldError = (fieldName: AddressFormFieldName): string | undefined => {
    const addressErrors = formErrors.address as Record<string, { message?: string }> | undefined;
    return addressErrors?.[fieldName]?.message;
  };

  const resolveFieldErrorMessage = (fieldName: AddressFormFieldName, fieldStateMessage?: string): string | undefined =>
    fieldStateMessage ?? (submitAttempted ? getAddressFieldError(fieldName) : undefined);

  const textInputHelpMessageMode = 'focusout' as const;

  const cityField = (
    <div className="flex flex-col">
      <FieldInfo label={t('labels.city')} htmlFor={`${formIdPrefix}-address-city`} />
      <div className="flex flex-row gap-2">
        <FormField
          control={form.control}
          name="address.city"
          render={({ field, fieldState }) => {
            const errorMessage = resolveFieldErrorMessage('city', fieldState.error?.message);
            return (
              <FormItem className="flex-1">
                <FormControl>
                  <TextInput
                    {...field}
                    id={`${formIdPrefix}-address-city`}
                    type="text"
                    placeholder={t('placeholders.city')}
                    helpMessage={errorMessage}
                    helpMessageMode={textInputHelpMessageMode}
                    helpMessageType={errorMessage ? 'error' : undefined}
                    helperTrackingProps={{
                      'data-track-helper': 'City',
                    }}
                    data-testid="text-input-address-city"
                    data-track-name="City"
                    data-track-privacy="Mask"
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />
      </div>
    </div>
  );

  const postcodeField = (
    <div className="flex flex-col">
      <FieldInfo label={t('labels.postcode')} htmlFor={`${formIdPrefix}-address-postcode`} />
      <div className="flex flex-row gap-2">
        <FormField
          control={form.control}
          name="address.postcode"
          render={({ field, fieldState }) => {
            const errorMessage = resolveFieldErrorMessage('postcode', fieldState.error?.message);
            return (
              <FormItem className="flex-1">
                <FormControl>
                  <TextInput
                    {...field}
                    id={`${formIdPrefix}-address-postcode`}
                    type="text"
                    placeholder={t('placeholders.postcode')}
                    helpMessage={errorMessage}
                    helpMessageMode={textInputHelpMessageMode}
                    helpMessageType={errorMessage ? 'error' : undefined}
                    helperTrackingProps={{
                      'data-track-helper': 'Postcode',
                    }}
                    data-testid="text-input-address-postcode"
                    data-track-name="Postcode"
                    data-track-privacy="Mask"
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />
      </div>
    </div>
  );

  if (!showManualEntry) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-row gap-2">
          <FormField
            control={form.control}
            name="addressLookup"
            render={({ field, fieldState }) => (
              <FormItem className="flex-1">
                <FormControl>
                  {lookupVariant === 'registration' ? (
                    <AddressLookupWithConfig
                      {...field}
                      id={`${formIdPrefix}-addressLookup`}
                      name={field.name}
                      value={field.value || ''}
                      onChange={(value: string) => {
                        field.onChange(value);
                        onLookupFieldChange?.(value);
                        void form.trigger('addressLookup');
                      }}
                      onAddressSelect={onAddressSelect}
                      onOpenChange={onLookupOpenChange}
                      onContentHeightChange={onDropdownHeightChange}
                      placeholder={t('placeholders.search')}
                      topActionElement={
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={onToggleEntryMode}
                          className="link-label-explanatory p-0 h-auto"
                          data-track-name={enterManuallyTrackingName}
                          data-testid="address-manually-button"
                        >
                          {enterManuallyLabel}
                        </Button>
                      }
                      helpMessage={fieldState.error?.message}
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      inputTrackingProps={{
                        'data-track-name': 'Address Finder',
                        'data-track-privacy': 'Mask',
                      }}
                      helperTrackingProps={{
                        'data-track-helper': 'Address Finder',
                      }}
                    />
                  ) : (
                    <AddressLookup
                      id={`${formIdPrefix}-addressLookup`}
                      value={(field.value as string) || ''}
                      onChange={value => field.onChange(value)}
                      onAddressSelect={onAddressSelect}
                      onOpenChange={onLookupOpenChange}
                      placeholder={t('placeholders.search')}
                      helpMessage={fieldState.error?.message}
                      helpMessageType={fieldState.error ? 'error' : undefined}
                      topActionElement={
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={onToggleEntryMode}
                          className="link-label-explanatory p-0 h-auto"
                          data-track-name={enterManuallyTrackingName}
                          data-testid="address-manually-button"
                        >
                          {enterManuallyLabel}
                        </Button>
                      }
                      inputTrackingProps={{
                        'data-track-name': 'Address Finder',
                        'data-track-privacy': 'Mask',
                      }}
                    />
                  )}
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <FieldInfo
          label={t('labels.houseNameNumber')}
          htmlFor={`${formIdPrefix}-house-name-number`}
          actionElement={
            <Button type="button" variant="ghost" onClick={onToggleEntryMode} className="link-label-explanatory p-0 h-auto" data-track-name="Address Search">
              {switchToSearchLabel}
            </Button>
          }
        />
        <div className="flex flex-row gap-2">
          <FormField
            control={form.control}
            name="address.houseNameNumber"
            render={({ field, fieldState }) => {
              const errorMessage = resolveFieldErrorMessage('houseNameNumber', fieldState.error?.message);
              return (
                <FormItem className="flex-1">
                  <FormControl>
                    <TextInput
                      {...field}
                      id={`${formIdPrefix}-house-name-number`}
                      type="text"
                      placeholder={t('placeholders.houseNameNumber')}
                      helpMessage={errorMessage}
                      helpMessageMode={textInputHelpMessageMode}
                      helpMessageType={errorMessage ? 'error' : undefined}
                      data-testid="text-input-house-name-number"
                      data-track-name="House Name/Number"
                      data-track-privacy="Mask"
                      helperTrackingProps={{
                        'data-track-helper': 'House Name/Number',
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </div>
      </div>

      <div className="flex flex-col">
        <FieldInfo label={t('labels.streetName')} htmlFor={`${formIdPrefix}-street-name`} />
        <div className="flex flex-row gap-2">
          <FormField
            control={form.control}
            name="address.streetName"
            render={({ field, fieldState }) => {
              const errorMessage = resolveFieldErrorMessage('streetName', fieldState.error?.message);
              return (
                <FormItem className="flex-1">
                  <FormControl>
                    <TextInput
                      {...field}
                      id={`${formIdPrefix}-street-name`}
                      type="text"
                      placeholder={t('placeholders.streetName')}
                      helpMessage={errorMessage}
                      helpMessageMode={textInputHelpMessageMode}
                      helpMessageType={errorMessage ? 'error' : undefined}
                      helperTrackingProps={{
                        'data-track-helper': 'Street Name',
                      }}
                      data-testid="text-input-street-name"
                      data-track-name="Street Name"
                      data-track-privacy="Mask"
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </div>
      </div>

      {collectsAddressLine2 && (
        <div className="flex flex-col">
          <FieldInfo label={t('labels.address2')} htmlFor={`${formIdPrefix}-address-line2`} />
          <div className="flex flex-row gap-2">
            <FormField
              control={form.control}
              name="address.line2"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <TextInput
                      {...field}
                      id={`${formIdPrefix}-address-line2`}
                      type="text"
                      placeholder={t('placeholders.address2')}
                      data-testid="text-input-address-line2"
                      data-track-name="Address 2"
                      data-track-privacy="Mask"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      )}

      {usesStackedCityPostcodeLayout ? (
        <>
          {cityField}
          <div className="flex flex-row gap-4 items-start">
            {showsStateSelect ? (
              <div className="flex flex-col flex-1 min-w-0">
                <FieldInfo label={t('labels.state')} htmlFor={`${formIdPrefix}-address-state`} />
                <div className="flex flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="address.state"
                    render={({ field, fieldState }) => {
                      const errorMessage = resolveFieldErrorMessage('state', fieldState.error?.message);
                      return (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Select
                              {...field}
                              id={`${formIdPrefix}-address-state`}
                              options={sortedStates}
                              placeholder={t('placeholders.state')}
                              helpMessage={errorMessage}
                              helpMessageType={errorMessage ? 'error' : undefined}
                              data-testid="select-address-state"
                              data-track-name="State"
                              data-track-privacy="Mask"
                              data-track-value={field.value}
                              data-track-helper="State"
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div className={showsStateSelect ? 'w-28 shrink-0' : 'w-1/2'}>{postcodeField}</div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cityField}
          {postcodeField}
        </div>
      )}
    </>
  );
}

export { RegistrationAddressFormFields, type RegistrationAddressFormFieldsProps };
